import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Activity, Boxes, Truck, ShoppingBag, Wallet, Layers, ListChecks,
  TrendingUp, AlertOctagon, Network, FlaskConical, Gauge, FileText,
  Database, Compass, Server, Zap, Cog,
  Sun, Moon, ChevronDown, User, Sparkles, Cpu, LogOut, ShieldCheck, KeyRound, ToggleRight,
  SlidersHorizontal,
} from "lucide-react";
import { useTheme } from "../lib/theme.jsx";
import { useRole } from "../lib/role.jsx";
import { useGate } from "../lib/gate.jsx";
import { cn } from "../lib/utils";
import NotificationDropdown from "./NotificationDropdown";
import HelpMenu from "./knowledge/HelpMenu";
import { SUBNAV, subnavFor } from "../lib/forecastApi";
import { APP_RELEASE } from "../lib/version";
import GlobalSearch from "./search/GlobalSearch";

// Sidebar group: the label opens the first child; children show while inside the group or when expanded.
function NavGroup({ item, pathname }) {
  const inside = pathname === item.gate || pathname.startsWith(`${item.gate}/`);
  const { role } = useRole();
  const kids = item.children === SUBNAV ? subnavFor(role?.id) : item.children;
  const [open, setOpen] = React.useState(inside);
  React.useEffect(() => { if (inside) setOpen(true); }, [inside]);
  return (
    <div data-testid={`${item.testid}-group`}>
      <div className={cn("flex items-center rounded-sm text-sm transition-colors", inside ? "text-primary" : "text-fg/85 hover:bg-surface-2 hover:text-fg")}>
        <NavLink to={item.to} data-testid={item.testid} className="flex-1 min-w-0 flex items-center gap-3 px-3 py-2">
          <item.icon size={16} strokeWidth={1.7} className="shrink-0" />
          <span className="leading-tight">{item.label}</span>
        </NavLink>
        <button type="button" data-testid={`${item.testid}-toggle`} aria-label={open ? "Collapse" : "Expand"} aria-expanded={open}
                onClick={() => setOpen(o => !o)} className="h-8 w-8 inline-flex items-center justify-center text-muted hover:text-fg">
          <ChevronDown size={13} className={cn("transition-transform", open ? "" : "-rotate-90")} />
        </button>
      </div>
      {open && (
        <div className="ml-[22px] border-l border-border pl-2 my-0.5 space-y-0.5">
          {kids.map(c => (
            <NavLink key={c.to} to={c.to} end={c.end} data-testid={`${item.testid}-${c.id}`}
                     className={({ isActive }) => cn("block px-2.5 py-1.5 rounded-sm text-[12.5px] leading-snug transition-colors",
                       isActive ? "bg-primary/10 text-primary" : "text-muted hover:text-fg hover:bg-surface-2")}>
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

const NAV = [
  { section: "Executive" },
  { to: "/morning-brief", label: "Morning Brief", icon: Sparkles },
  { to: "/", label: "Command Center", icon: LayoutDashboard, end: true },
  { to: "/branches", label: "Branch Performance", icon: Activity },
  { to: "/inventory", label: "Inventory Intelligence", icon: Boxes },
  { to: "/procurement", label: "Procurement & Supply", icon: Truck },
  { to: "/omnichannel", label: "Omnichannel Sales", icon: ShoppingBag },
  { to: "/merchandising", label: "Merchandising", icon: Layers },
  { to: "/financial", label: "Financial", icon: Wallet },
  { to: "/reports", label: "Reports Center", icon: FileText },
  { section: "AI Layer" },
  { to: "/forecast/overview", gate: "/forecast", label: "Forecast / Prediction Models", icon: TrendingUp, children: SUBNAV, testid: "nav-forecast" },
  { to: "/anomalies", label: "Anomaly Detection", icon: AlertOctagon },
  { to: "/operations", label: "Daily Operations", icon: ListChecks },
  { section: "Digital Twin" },
  { to: "/twin", label: "Operational Digital Twin", icon: Network },
  { to: "/workbench", label: "Simulation Workbench", icon: FlaskConical },
  { section: "Governance" },
  { to: "/kpi", label: "KPI Framework", icon: Gauge },
  { section: "Admin Console" },
  { to: "/admin/pipeline", label: "Live Data Pipeline", icon: Server },
  { to: "/admin/explorer", label: "Semantic Data Explorer", icon: Compass },
  { to: "/admin/data-management", label: "Data Management Console", icon: Database },
  { to: "/admin/engines", label: "Inventory Engines Admin", icon: Cog },
  { to: "/admin/classification", label: "Classification Rules", icon: SlidersHorizontal },
  { to: "/admin/cost-transparency", label: "Cost Transparency", icon: Wallet },
  { to: "/admin/api-keys", label: "API Keys & Integrations", icon: KeyRound },
  { to: "/admin/modules", label: "Module Visibility", icon: ToggleRight },
];

export default function AppShell({ children, onOpenCopilot }) {
  const { theme, toggle } = useTheme();
  const { role, user, canAccess } = useRole();
  const { signOut } = useGate();
  const location = useLocation();
  const [roleOpen, setRoleOpen] = React.useState(false);

  React.useEffect(() => {
    const close = () => setRoleOpen(false);
    if (roleOpen) {
      window.addEventListener("click", close);
      return () => window.removeEventListener("click", close);
    }
  }, [roleOpen]);

  // Hidden access: Shift+? opens the platform explainer in a new tab.
  React.useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toUpperCase();
      const isTyping = tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable;
      if (isTyping) return;
      if (e.shiftKey && (e.key === "?" || e.code === "Slash")) {
        e.preventDefault();
        window.open("/explainer.html", "_blank", "noopener,noreferrer");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen flex bg-bg text-fg">
      {/* Sidebar */}
      <aside data-testid="app-sidebar" className="hidden lg:flex w-[260px] shrink-0 flex-col border-r border-border bg-surface sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-border flex items-center gap-3">
          <img src="/1rotary-logo.png" alt="1Rotary" className="h-9 w-auto" />
          <div className="leading-tight">
            <div className="font-display font-semibold tracking-tight text-[15px]">1Rotary</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted">Control Tower</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {NAV.map((item, i) => {
            if (item.section) {
              // Only render section header if at least one child route below it is allowed.
              // Find the next NAV items that belong to this section (until next section).
              const childItems = [];
              for (let j = i + 1; j < NAV.length; j++) {
                if (NAV[j].section) break;
                childItems.push(NAV[j]);
              }
              const anyVisible = childItems.some(c => canAccess(c.gate || c.to));
              if (!anyVisible) return null;
              return (
                <div key={`s-${i}`} className="px-3 mt-4 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-subtle">
                  {item.section}
                </div>
              );
            }
            if (!canAccess(item.gate || item.to)) return null;
            if (item.children) return <NavGroup key={item.to} item={item} pathname={location.pathname} />;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  cn(
                    "group flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors",
                    isActive ? "bg-primary/10 text-primary border-l-2 border-primary -ml-[2px] pl-[14px]" : "text-fg/85 hover:bg-surface-2 hover:text-fg"
                  )
                }
              >
                <item.icon size={16} strokeWidth={1.7} className="shrink-0" />
                <span className="truncate">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-border p-3 text-[11px] text-muted leading-relaxed">
          <div className="font-display text-fg/90 mb-0.5 italic">"Cooling the Nation,</div>
          <div className="font-display text-fg/90 italic">Creating Opportunities."</div>
          <div className="mt-2 flex items-center gap-1.5"><span className="live-dot" /><span>Live · {new Date().toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}</span></div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header data-testid="app-topbar" className="sticky top-0 z-30 backdrop-blur bg-surface/85 border-b border-border">
          <div className="flex items-center gap-3 px-4 lg:px-6 h-14">
            <div className="lg:hidden font-display font-semibold">1Rotary</div>
            <div className="hidden md:flex min-w-0 items-center gap-2 text-xs text-muted">
              <Cpu size={14} className="text-primary shrink-0" />
              <span data-testid="app-version" className="font-mono-tabular tracking-wider uppercase whitespace-nowrap">{APP_RELEASE}</span>
              <span className="text-subtle">/</span>
              <span className="font-mono-tabular truncate">{currentPath(location.pathname)}</span>
            </div>
            <div className="flex-1" />

            {/* Search — SKU, branch, supplier + AI Search */}
            <GlobalSearch />

            {/* AI Copilot */}
            <button
              data-testid="open-copilot"
              onClick={onOpenCopilot}
              className="hidden md:inline-flex items-center gap-2 h-9 px-3 text-sm whitespace-nowrap shrink-0 border border-border bg-surface-2 hover:bg-primary/10 hover:border-primary/40 hover:text-primary transition-colors rounded-sm"
            >
              <Sparkles size={14} />
              <span className="hidden lg:inline">AI Copilot</span>
            </button>

            {/* Help → Knowledge Center (hidden module, every role) */}
            <HelpMenu />

            {/* Notifications */}
            <NotificationDropdown />

            {/* Theme toggle */}
            <button
              data-testid="theme-toggle"
              onClick={toggle}
              className="h-9 w-9 inline-flex items-center justify-center border border-border bg-surface-2 hover:bg-surface rounded-sm"
              aria-label="Toggle theme"
              title={theme === "dark" ? "Switch to Day" : "Switch to Night"}
            >
              {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            </button>

            {/* Role badge (locked) + profile menu */}
            <div className="relative">
              <button
                data-testid="role-badge"
                onClick={(e) => { e.stopPropagation(); setRoleOpen(o => !o); }}
                className="h-9 inline-flex items-center gap-2 pl-2 pr-3 border border-border bg-surface-2 hover:bg-surface rounded-sm"
                title={user ? `${user.name} · ${user.employeeId}` : "Profile"}
              >
                <div className="w-6 h-6 rounded-sm bg-primary/15 text-primary flex items-center justify-center"><User size={13} /></div>
                <div className="text-left leading-tight hidden sm:block">
                  <div className="text-[11px] font-semibold uppercase tracking-wider">{role?.short || "—"}</div>
                  <div className="text-[10px] text-muted truncate max-w-[140px]">{role?.scope || ""}</div>
                </div>
                <ChevronDown size={13} className="text-muted" />
              </button>
              {roleOpen && (
                <div className="absolute right-0 top-11 w-80 bg-surface border border-border rounded-md shadow-xl z-50 overflow-hidden animate-fade-in" data-testid="profile-menu">
                  <div className="px-4 py-3 border-b border-border bg-surface-2/40">
                    <div className="text-[10px] uppercase tracking-widest text-muted mb-1.5 inline-flex items-center gap-1.5">
                      <ShieldCheck size={10} className="text-success" /> Signed In · Role-Locked
                    </div>
                    <div className="font-display font-semibold text-sm leading-tight">{user?.name || "—"}</div>
                    <div className="text-[11px] text-muted mt-0.5">{user?.roleLabel}</div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-3 text-[10px]">
                      <div><span className="text-muted">Emp ID</span><div className="font-mono-tabular text-primary">{user?.employeeId}</div></div>
                      <div><span className="text-muted">Dept</span><div>{user?.department}</div></div>
                      <div className="col-span-2"><span className="text-muted">Scope</span><div>{user?.scope}</div></div>
                      {user?.branchName && (
                        <div className="col-span-2"><span className="text-muted">Branch</span><div className="text-primary">{user.branchName}</div></div>
                      )}
                    </div>
                  </div>
                  <button
                    data-testid="sign-out"
                    onClick={() => { setRoleOpen(false); signOut(); }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-critical/10 hover:text-critical flex items-center gap-2"
                  >
                    <LogOut size={13} />
                    <span>Sign out of Control Tower</span>
                  </button>
                </div>
              )}
            </div>
          </div>
          {/* Ticker */}
          <Ticker />
        </header>

        <main className="flex-1 px-4 lg:px-6 py-6 lg:py-8 max-w-[1680px] w-full mx-auto">
          {children}
        </main>
        <footer data-testid="app-footer" className="px-6 py-4 border-t border-border text-[11px] text-muted flex flex-wrap items-center justify-between gap-3">
          <div>1Rotary Trading Corporation · Unit 1018 High Street South, BGC, Taguig City</div>
          <div className="font-mono-tabular">100+ Branches · Luzon · Visayas · Mindanao</div>
          <div className="flex items-center gap-2">
            <span>
              Developed by{" "}
              <a
                data-testid="footer-asterisk-link"
                href="https://www.asterisk.com.sg"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                Asterisk Data and AI Labs
              </a>
            </span>
            <a
              data-testid="footer-explainer-link"
              href="/explainer.html"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open platform brief"
              title="Platform brief  ·  Shift + ?"
              className="group relative inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-subtle/60 hover:text-primary transition-colors cursor-pointer"
            >
              <span className="block w-1 h-1 rounded-full bg-current group-hover:scale-150 transition-transform" />
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}

function currentPath(p) {
  return p === "/" ? "/command-center" : p;
}

import { PILOT_ALERTS } from "../lib/dataPilot";
function Ticker() {
  const items = [...PILOT_ALERTS, ...PILOT_ALERTS];
  return (
    <div className="h-7 border-t border-border bg-surface-2/60 overflow-hidden relative">
      <div className="absolute inset-y-0 left-0 px-3 flex items-center gap-2 z-10 bg-surface-2 border-r border-border">
        <span className="live-dot" />
        <span className="text-[10px] uppercase tracking-widest font-semibold text-muted">Live Feed</span>
      </div>
      <div className="marquee-track flex items-center gap-8 whitespace-nowrap h-full pl-32 text-[11px]">
        {items.map((a, i) => (
          <span key={i} className="inline-flex items-center gap-2">
            <span className={cn("w-1.5 h-1.5 rounded-full",
              a.severity === "critical" ? "bg-critical" : a.severity === "warning" ? "bg-warning" : "bg-info")} />
            <span className="text-muted font-mono-tabular">{a.timeAgo}</span>
            <span className="text-fg/90">{a.text}</span>
            <span className="text-subtle">·</span>
          </span>
        ))}
      </div>
    </div>
  );
}
